/**
 * `<ProDocDocumentDetail>` — one document, opened as what is true (UI_SYSTEM
 * §5): the file beside its **record** — title, type, the date on it, notes and
 * custom fields; where it is filed and whose it is; its versions; and its
 * **history**, every edit with old → new and who made it, and every read with
 * why (FR-8, FR-9, FR-11, FR-19). Nothing a parser would fill (ADR-0008).
 *
 * It is the **Storage** capability's screen (ADR-0010): the bytes come back by
 * a logged, five-minute presigned read, never by a key; a replacement file is a
 * **new version**, the old one kept; `can_delete` means archive, and only an
 * administrator over the whole organisation restores (D11). The verbs follow
 * the reader's ProDoc verbs and D12's own-upload rule (`mayEdit`), and every
 * one the service would refuse is simply not offered.
 *
 * W4 adds the **review state** (FR-20): a badge beside storage's status (D1),
 * what the last decision said, *Request review* for whoever may edit an
 * `uploaded` document, and — for a reviewer, when the host gives the way —
 * the door into the review queue.
 *
 * W6 adds **Share** (the share dialog) and **Retention** — how long it is kept,
 * the floors and their citations, the flag when it is past its date, and the
 * administrator's override behind step-up. An **expired** document is a
 * tombstone: its record and history stay, its file is gone, and nothing offers
 * to read it (FR-26).
 *
 * W9 says **whom an app acted for** (FR-40) wherever an app is the actor — the
 * history, the versions, the record: *"ProDesk for Asha Rao"*, *"ProDesk, for a
 * client contact"* — and an **imported** version (FR-42) as *"Imported,
 * originally uploaded <date>"*, its imported reads at their original times.
 *
 * Engine W15 adds the **Extractions** tab (`extractions-panel.tsx`): what the
 * engine read from it when asked, and *Read it as…* where FR-E3 allows — the
 * record itself still holds nothing a parser filled (ADR-0008).
 *
 * W23 adds the **Personal data** tab (`personal-data-panel.tsx`, FR-51, FR-52):
 * what the current version holds of an individual's personal data, masked, and
 * *Reveal* for whoever holds `personal_data:can_view`. The panel is mounted only
 * while its tab is open and is keyed by the version, so a revealed value is gone
 * the moment the person moves on.
 *
 * W37 (C8, ADR-0025) says **a split** (`split.tsx`): a part's pages of its original, a
 * split original's parts and *Undo the split*, an original the engine was not sure of.
 *
 * W30 (FR-67, ADR-0021) puts **a protected file's password** beside its type:
 * *Password saved* — kept sealed, used for the reader, never shown; final for
 * the version, so there is nothing to change or forget — or, for a person with
 * `document:can_update`, the box that saves it. With it saved the preview is
 * the engine's drawing of each page, and nothing asks.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DescriptionList,
  Detail,
  Dialog,
  DropdownMenu,
  EmptyState,
  FileInput,
  Input,
  PageHeader,
  SearchInput,
  Select,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
  TabPanel,
  Tabs,
  Textarea,
  formatDate,
  formatDateTime,
  useToast,
} from '@fiveox/ui'
import { Download, Ellipsis, Link2, Plus, Share2, Trash2 } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  ApiError,
  DOCUMENT,
  EXTRACTION,
  PERSONAL_DATA,
  SHARE,
  TAG,
  acceptFor,
  actedForLabel,
  appName,
  documentTypeLabel,
  keptByOf,
  formatBytes,
  mayEdit,
  mayRequestReview,
  mayRestore,
  mayReview,
  passwordSavedOf,
  type AuditEntry,
  type CustomValue,
  type DocumentPatch,
  type DocumentTypeOption,
  type DocumentVersion,
  type ProDocClient,
  type ProDocDocument,
  type RelationKind,
} from './prodoc-client'
import { FileMark, ReviewBadge, StatusBadge, mediaLabel, plural, reviewLabel, when } from './bits'
import { FolderPicker } from './folder-dialogs'
import {
  errorText,
  uploaderLabel,
  useLoad,
  useProDocAccess,
  useProDocClient,
  useWorkspaces,
  versionUploaderLabel,
  type HostProps,
  type Loaded as Loading,
} from './hooks'
import type { ExtractionRecord } from './extraction-model'
import { ExtractionsPanel } from './extractions-panel'
import { PERSONAL_DATA_KIND_WORDS } from './personal-data'
import { PersonalDataPanel } from './personal-data-panel'
import { Preview } from './preview'
import { RetentionBadge, RetentionPanel } from './retention-panel'
import { ShareDialog, type SharingHostProps } from './share-dialog'
import { SplitPanel } from './split'
import { DocumentTags, useTags } from './tag-picker'
import { PasswordStatus } from './unlock'

export interface ProDocDocumentDetailProps extends HostProps, SharingHostProps {
  documentId: string
  /** Rendered as the header's way back — the host's `<Link to="/documents">`. */
  back?: ReactNode
  /** After the document is archived: the host navigates away. */
  onArchived?: (document: ProDocDocument) => void
  /** After an edit, a move, a new version or a restore. */
  onChanged?: (document: ProDocDocument) => void
  /** Once the record has arrived — the host's Recent list, say. */
  onLoaded?: (document: ProDocDocument) => void
  /** Inside a drawer the header is the drawer's; `compact` drops the page header. */
  compact?: boolean
  /**
   * The host's way into its review screen for this document — offered to a
   * reviewer while it waits for a decision. Not offered without it.
   */
  onReview?: (document: ProDocDocument) => void
  /** Open another document — a related one — the host's way (a route). Without it, related documents are named, not links. */
  onOpenDocument?: (document: ProDocDocument) => void
  /**
   * Engine W15 (FR-E13, FR-E20): open one of its extractions — the host's
   * route to `<ProDocExtractionReview>`. Without it the Extractions tab lists
   * them and does not link.
   */
  onOpenExtraction?: (document: ProDocDocument, extraction: ExtractionRecord) => void
  /** How soon a parse it started is first asked about again. A test passes a few milliseconds. */
  jobPollMs?: number
}

type Tab = 'details' | 'versions' | 'related' | 'extractions' | 'personal_data' | 'retention' | 'history'

export function ProDocDocumentDetail(props: ProDocDocumentDetailProps) {
  const { documentId, back, onArchived, onChanged, onLoaded, onReview, onOpenDocument, compact = false, userId } = props
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  const { toast } = useToast()
  const doc = useLoad(() => client.documents.get(documentId), [client, documentId])
  const options = useLoad(() => client.documents.options(), [client])
  const workspaces = useWorkspaces(client)
  const tags = useTags(client, access.can, access.loading)
  // Bumped after every change, so the versions and the history re-read.
  const [revision, setRevision] = useState(0)
  // Read once here, not only in its tab: D12's rule turns on who uploaded the
  // **current** version, which the single read does not carry.
  const versions = useLoad(() => client.documents.versions.list(documentId), [client, documentId, revision])
  const [tab, setTab] = useState<Tab>('details')
  const [dialog, setDialog] = useState<'version' | 'move' | 'archive' | 'restore' | 'share' | null>(null)
  const [busy, setBusy] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [dialogError, setDialogError] = useState('')

  useEffect(() => {
    if (doc.data) onLoaded?.(doc.data)
    // Once per arrival; the host's callback identity is not the question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.data])

  const closeDialog = () => {
    setDialog(null)
    setDialogError('')
  }

  /**
   * A fresh record from the service: shown, announced, and the history re-read.
   * An edit's answer carries no breadcrumb — only the single read does — so the
   * trail is kept while the folder is the same, and read again when it moved.
   */
  const changed = (next: ProDocDocument) => {
    const prev = doc.data
    const sameFolder = prev !== null && (next.folder_id ?? null) === (prev.folder_id ?? null)
    const kept = next.breadcrumb === undefined && sameFolder ? { ...next, breadcrumb: prev.breadcrumb } : next
    doc.set(kept)
    onChanged?.(kept)
    setRevision((n) => n + 1)
    if (kept.breadcrumb === undefined && kept.folder_id) {
      void client.documents
        .get(kept.id)
        .then((read) => doc.set(read))
        .catch(() => {})
    }
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setDialogError('')
    try {
      await fn()
    } catch (e) {
      setDialogError(refusalText(e))
    } finally {
      setBusy(false)
    }
  }

  if (doc.loading) {
    return (
      <SkeletonRegion label="Loading this document" className="space-y-6">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-7 w-80" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <Skeleton className="h-[60vh] rounded-2xl lg:col-span-3" />
          <Card className="space-y-3 p-6 lg:col-span-2">
            <SkeletonText w="w-40" />
            <SkeletonText />
            <SkeletonText w="w-2/3" />
          </Card>
        </div>
      </SkeletonRegion>
    )
  }

  const d = doc.data
  if (!d) {
    // Out of sight answers NOT_FOUND, never FORBIDDEN (constitution §3), so
    // one sentence covers both. Anything else is a failure, said as one.
    const missing = /not found|does not exist/i.test(doc.error) || !doc.error
    return (
      <EmptyState
        title={missing ? 'Document not found' : 'This document could not be loaded'}
        description={missing ? 'It may have been archived, or it is outside the workspaces you see.' : doc.error}
        action={
          missing ? (
            back
          ) : (
            <Button variant="outline" onClick={() => void doc.reload()}>
              Try again
            </Button>
          )
        }
      />
    )
  }

  const types = options.data?.document_types ?? []
  const workspaceName = workspaces.nameOf(d.workspace_id)
  // Who stored the first version — an app said with whom it acted for (FR-40), once the versions say so.
  const first = versions.data?.find((v) => v.version_no === 1)
  const uploader = first
    ? versionUploaderLabel(first, userId)
    : d.uploaded_party
      ? actedForLabel({ service: d.owner_app, user_id: null, party: d.uploaded_party }, userId)
      : uploaderLabel(d.uploaded_by ?? '', userId)
  const current =versions.data?.find((v) => v.is_current || v.id === d.current_version_id) ?? d.current_version
  // W30 (FR-67): the current version's password is saved — the record says so, or its version does.
  const passwordSaved = passwordSavedOf(d) || current?.password_saved === true
  // D12 turns on whose the current version is: the record says so from W4
  // (`current_version_uploaded_by`); from an older service an uploader's edit waits for the versions.
  const knowsUploader = versions.data !== null || d.current_version_uploaded_by !== undefined || access.can(DOCUMENT, 'can_update')
  const editable = knowsUploader && mayEdit(access.can, { ...d, current_version: current }, userId)
  const archivable = access.can(DOCUMENT, 'can_delete') && d.status === 'stored'
  const restorable = mayRestore(access.can) && d.status === 'archived'
  // Asking for a review is own+create (plan §5.2), so it knows whose the current version is as an edit does.
  const requestable = knowsUploader && mayRequestReview(access.can, { ...d, current_version: current }, userId)
  const reviewable = Boolean(onReview) && mayReview(access.can) && d.status === 'stored' && d.review_state === 'needs_review'
  // W6: share a stored document (`share:can_create`), or see who it is shared with (`share:can_view`).
  const shareable = d.status === 'stored' && (access.can(SHARE, 'can_create') || access.can(SHARE, 'can_view'))
  // W9 (D46): a sibling's document no rule of the organisation's matches is kept by its app — no date of ProDoc's decides its end.
  const kept = keptByOf(d)
  const afterArchive = kept
    ? `${appName(kept)} keeps its life, so ProDoc never removes them`
    : 'the organisation’s retention rule decides what happens to them'

  const requestReview = async () => {
    setRequesting(true)
    try {
      changed(await client.documents.review.request(d.id))
      toast('Sent for review. A reviewer decides it from the Review queue.')
    } catch (e) {
      toast({ message: refusalText(e), tone: 'error' })
    } finally {
      setRequesting(false)
    }
  }

  const download = async () => {
    try {
      const handle = await client.documents.file(d.id, 'download')
      saveBlob(await handle.blob(), handle.fileName ?? d.file_name)
    } catch (e) {
      toast({ message: errorText(e, 'The file could not be downloaded.'), tone: 'error' })
    }
  }

  const menu = [
    ...(editable
      ? [
          { id: 'version', label: 'Upload a new version…', onSelect: () => setDialog('version') },
          { id: 'move', label: 'Move to a folder or workspace…', onSelect: () => setDialog('move') },
        ]
      : []),
    ...(archivable ? [{ id: 'archive', label: 'Archive…', tone: 'danger' as const, separatorBefore: true, onSelect: () => setDialog('archive') }] : []),
    ...(restorable ? [{ id: 'restore', label: 'Restore…', onSelect: () => setDialog('restore') }] : []),
  ]

  const actions = (
    <>
      {requestable && (
        <Button variant="outline" onClick={() => void requestReview()} loading={requesting} loadingText="Sending…">
          Request review
        </Button>
      )}
      {shareable && (
        <Button variant="outline" onClick={() => setDialog('share')}>
          <Share2 size={16} aria-hidden />
          Share
        </Button>
      )}
      {d.status !== 'expired' && (
        <Button variant="outline" onClick={() => void download()}>
          <Download size={16} aria-hidden />
          Download
        </Button>
      )}
      {menu.length > 0 && (
        <DropdownMenu
          label="More actions"
          items={menu}
          align="end"
          trigger={({ open: _open, ...triggerProps }) => (
            <Button {...triggerProps} variant="outline" aria-label="More actions">
              <Ellipsis size={18} aria-hidden />
            </Button>
          )}
        />
      )}
    </>
  )

  const meta = (
    <>
      <StatusBadge status={d.status} />
      <ReviewBadge state={d.review_state} />
      <RetentionBadge doc={d} />
      <Badge tone="neutral">{documentTypeLabel(d.document_type, types)}</Badge>
      {d.encrypted && <Badge tone="warning">Encrypted</Badge>}
      {/* FR-58, FR-67: the same word as the library's column, beside the type — *Password saved*, or the box that saves it. */}
      {((d.password && d.password !== 'not_required') || passwordSaved) && (
        <PasswordStatus
          client={client}
          doc={d}
          saved={passwordSaved}
          mayUpdate={access.can(DOCUMENT, 'can_update') && d.status === 'stored'}
          offerWhenUnlocked
          // The record says how it stands now — re-read quietly, so the page does not reload around the person.
          onSaved={() =>
            void client.documents
              .get(d.id)
              .then(changed)
              .catch(() => {})
          }
        />
      )}
      <span className="text-muted-foreground text-sm">
        {mediaLabel(d.media_type)} · {formatBytes(d.size_bytes)}
        {d.version_count > 1 ? ` · ${plural(d.version_count, 'version')}` : ''}
        {workspaceName ? ` · ${workspaceName}` : ''} · uploaded by {uploader}, {formatDateTime(d.created_at)}
      </span>
    </>
  )

  return (
    <div className="space-y-6">
      {compact ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <FileMark mediaType={d.media_type} />
            <div className="min-w-0">
              <h2 className="text-foreground truncate text-lg font-semibold" title={d.title}>
                {d.title}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">{meta}</div>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">{actions}</div>
        </div>
      ) : (
        <PageHeader back={back} title={d.title} meta={meta} actions={actions} />
      )}

      {d.status === 'archived' && (
        <Alert tone="info">
          <p>
            Archived {formatDateTime(d.archived_at ?? d.updated_at)}. It has left every list; its bytes, versions and history stay, and {afterArchive}.
            Nothing is deleted.
          </p>
          {restorable && (
            <Button variant="link" onClick={() => setDialog('restore')}>
              Restore it
            </Button>
          )}
        </Alert>
      )}

      {d.status === 'expired' && (
        <Alert tone="info">
          <p>
            Its file was removed{d.expired_at ? ` ${formatDateTime(d.expired_at)}` : ''}
            {d.expiry_reason === 'erasure' ? ' on a personal-data erasure request' : d.expiry_reason === 'retention' ? ' when its retention period ended' : ''}. The record and
            its history are kept, so what it was and who read it stay on file.
          </p>
        </Alert>
      )}

      {d.status === 'stored' && d.review_state === 'needs_review' && (
        <Alert tone="warning">
          <p>
            Waiting for review since {formatDateTime(d.review_requested_at ?? d.updated_at)}. A reviewer approves or rejects it, and whoever uploaded
            it is told.
          </p>
          {reviewable && (
            <Button variant="link" onClick={() => onReview?.(d)}>
              Review it now
            </Button>
          )}
        </Alert>
      )}

      {d.status === 'stored' && d.review_state === 'rejected' && (
        <Alert tone="error">
          <p>
            Rejected{d.reviewed_at ? ` ${formatDateTime(d.reviewed_at)}` : ''}
            {d.review_comment ? `: “${d.review_comment}”` : '.'} A new version of it goes back for review.
          </p>
          {editable && (
            <Button variant="link" onClick={() => setDialog('version')}>
              Upload a new version
            </Button>
          )}
        </Alert>
      )}

      {/* W37 (C8): a part says whose pages it holds; a split original lists its parts. */}
      <SplitPanel client={client} doc={d} can={access.can} onOpenDocument={onOpenDocument} onChanged={changed} />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-5">
        <Card padding="md" className="lg:col-span-3">
          {d.status === 'expired' ? (
            <EmptyState variant="outline" title="The file is gone" description="An expired document keeps its record and history. Its bytes were removed from the store and cannot be read again." />
          ) : (
            <Preview client={client} doc={d} passwordSaved={passwordSaved} onDownload={() => void download()} />
          )}
        </Card>

        <Card className="lg:col-span-2">
          <div className="px-5 pt-4">
            <Tabs
              idBase="prodoc-detail"
              tabs={[
                { id: 'details', label: 'Details' },
                { id: 'versions', label: 'Versions', badge: d.version_count > 1 ? <Badge size="sm">{d.version_count}</Badge> : undefined },
                { id: 'related', label: 'Related' },
                // Engine W15: what was read from it — for whoever may read an extraction (FR-E39).
                ...(access.can(EXTRACTION, 'can_view') ? [{ id: 'extractions', label: 'Extractions' }] : []),
                // W23 (FR-51): what it holds of a person's data, masked — for whoever sees it, from a service that scans.
                ...(d.personal_data
                  ? [
                      {
                        id: 'personal_data',
                        label: 'Personal data',
                        badge:
                          d.personal_data.state === 'found' && d.personal_data.kinds[0] ? (
                            <Badge size="sm" tone="warning">
                              {PERSONAL_DATA_KIND_WORDS[d.personal_data.kinds[0]]}
                            </Badge>
                          ) : undefined,
                      },
                    ]
                  : []),
                { id: 'retention', label: 'Retention' },
                { id: 'history', label: 'History' },
              ]}
              active={tab}
              onChange={(next) => setTab(next as Tab)}
            />
          </div>
          <CardBody className="pt-4">
            <TabPanel idBase="prodoc-detail" id="details" active={tab}>
              <div className="mb-5">
                <DocumentTags
                  client={client}
                  doc={d}
                  tags={tags}
                  editable={editable}
                  canCreate={access.can(TAG, 'can_create')}
                  onSaved={changed}
                  onTagsChanged={() => void tags.reload()}
                />
              </div>
              <DetailsPanel
                key={`${d.id}:${d.updated_at}`}
                client={client}
                doc={d}
                types={types}
                editable={editable}
                workspaceName={workspaceName}
                uploader={uploader}
                onSaved={(next) => {
                  changed(next)
                  toast('Saved.')
                }}
              />
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="versions" active={tab}>
              <VersionsPanel client={client} doc={d} versions={versions} userId={userId} canAdd={editable} onAdd={() => setDialog('version')} />
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="related" active={tab}>
              {tab === 'related' && (
                <RelatedPanel client={client} doc={d} editable={editable} revision={revision} onOpen={onOpenDocument} onChanged={() => setRevision((n) => n + 1)} />
              )}
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="retention" active={tab}>
              <RetentionPanel
                client={client}
                doc={d}
                can={access.can}
                requestStepUp={props.requestStepUp}
                onChanged={(next) => {
                  changed(next)
                  toast(next.retention_override_until ? `Kept until ${formatDate(next.retention_override_until)}.` : 'Back to the organisation’s rule.')
                }}
              />
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="extractions" active={tab}>
              {tab === 'extractions' && access.can(EXTRACTION, 'can_view') && (
                <ExtractionsPanel client={client} doc={passwordSaved ? { ...d, password_saved: true } : d} can={access.can} types={types} onOpenExtraction={props.onOpenExtraction} jobPollMs={props.jobPollMs} />
              )}
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="personal_data" active={tab}>
              {/* Mounted only while open, keyed by the version: a revealed value is gone as soon as the person moves on. */}
              {tab === 'personal_data' && d.personal_data && (
                <PersonalDataPanel key={`${d.id}:${d.current_version_id}`} client={client} doc={d} canReveal={access.can(PERSONAL_DATA, 'can_view')} />
              )}
            </TabPanel>
            <TabPanel idBase="prodoc-detail" id="history" active={tab}>
              <HistoryPanel
                client={client}
                doc={d}
                revision={revision}
                userId={userId}
                types={types}
                nameOf={workspaces.nameOf}
                tagName={(id) => tags.list.find((t) => t.id === id)?.name ?? null}
              />
            </TabPanel>
          </CardBody>
        </Card>
      </div>

      <VersionDialog
        open={dialog === 'version'}
        current={d}
        accept={options.data ? options.data.media_types : []}
        busy={busy}
        error={dialogError}
        onClose={closeDialog}
        onSave={(file) =>
          void run(async () => {
            const next = await client.documents.versions.add(d.id, { file })
            changed(next)
            closeDialog()
            setTab('versions')
            toast(`Version ${next.version_count ?? d.version_count + 1} is now the current file.`)
          })
        }
      />

      <MoveDialog
        open={dialog === 'move'}
        client={client}
        current={d}
        app={options.data?.app ?? 'prodoc'}
        busy={busy}
        error={dialogError}
        onClose={closeDialog}
        onSave={(workspace_id, folder_id) =>
          void run(async () => {
            let next = d
            // Another workspace first — the document lands in its top level (FR-16) — then the folder in it.
            if (workspace_id !== d.workspace_id) next = await client.documents.update(d.id, { workspace_id })
            if (folder_id !== (next.folder_id ?? null)) next = await client.documents.update(d.id, { folder_id })
            changed(next)
            closeDialog()
            const moved = workspaces.nameOf(next.workspace_id)
            toast(folder_id ? 'Filed in the folder.' : moved ? `Moved to the top level of ${moved}.` : 'Moved.')
          })
        }
      />

      <ShareDialog
        client={client}
        open={dialog === 'share'}
        target={{ documents: [{ id: d.id, title: d.title }], folders: [] }}
        can={access.can}
        userId={userId}
        listMembers={props.listMembers}
        requestStepUp={props.requestStepUp}
        shareLinkBase={props.shareLinkBase}
        onClose={closeDialog}
      />

      <ConfirmDialog
        open={dialog === 'archive'}
        onClose={closeDialog}
        title={`Archive “${d.title}”?`}
        tone="danger"
        confirmLabel="Archive document"
        cancelLabel="Keep it"
        busy={busy}
        error={dialogError}
        onConfirm={() =>
          void run(async () => {
            const next = await client.documents.archive(d.id)
            changed(next)
            closeDialog()
            toast(`Archived “${d.title}”.`)
            onArchived?.(next)
          })
        }
      >
        <p>It leaves every list, count and filter for everyone in this organisation; an administrator can restore it.</p>
        <p>
          {kept
            ? `Nothing is deleted: the file, its versions and its history stay — ${appName(kept)} keeps its life, so ProDoc never removes them.`
            : 'Nothing is deleted: the file, its versions and its history stay until the organisation’s retention rule decides.'}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'restore'}
        onClose={closeDialog}
        title={`Restore “${d.title}”?`}
        confirmLabel="Restore document"
        cancelLabel="Leave it archived"
        busy={busy}
        error={dialogError}
        onConfirm={() =>
          void run(async () => {
            const next = await client.documents.restore(d.id)
            changed(next)
            closeDialog()
            toast(`Restored “${d.title}”.`)
          })
        }
      >
        <p>It comes back to the lists of everyone who sees {workspaceName ?? 'its workspace'}, exactly as it was when it was archived.</p>
      </ConfirmDialog>
    </div>
  )
}

/* --------------------------------------------------------------- details */

/** A custom field as the form holds it: text, with the stored value kept when the text is unchanged. */
interface FieldRow {
  id: number
  key: string
  text: string
  original?: CustomValue
}

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/
const MAX_FIELDS = 50

const shown = (v: CustomValue | undefined): string => (v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v))

/**
 * The record a person may change (FR-11, plan §5.2): title, type, the date on
 * the document, notes and custom fields. Read-only for whoever `mayEdit`
 * refuses. The file name is a version's fact and is not here.
 */
function DetailsPanel({
  client,
  doc,
  types,
  editable,
  workspaceName,
  uploader,
  onSaved,
}: {
  client: ProDocClient
  doc: ProDocDocument
  types: DocumentTypeOption[]
  editable: boolean
  workspaceName: string | null
  uploader: string
  onSaved: (next: ProDocDocument) => void
}) {
  const [title, setTitle] = useState(doc.title)
  const [type, setType] = useState(doc.document_type)
  const [date, setDate] = useState(doc.document_date ?? '')
  const [notes, setNotes] = useState(doc.notes ?? '')
  const [rows, setRows] = useState<FieldRow[]>(() =>
    Object.entries(doc.custom_fields ?? {}).map(([key, value], i) => ({ id: i, key, text: shown(value), original: value })),
  )
  const [nextId, setNextId] = useState(rows.length)
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const custom: Record<string, CustomValue> = {}
  for (const r of rows) {
    if (!r.key.trim()) continue
    custom[r.key.trim()] = r.original !== undefined && shown(r.original) === r.text ? r.original : r.text.trim()
  }
  const patch: DocumentPatch = {}
  if (title.trim() !== doc.title) patch.title = title.trim()
  if (type !== doc.document_type) patch.document_type = type
  if ((date || null) !== (doc.document_date ?? null)) patch.document_date = date || null
  if ((notes.trim() || null) !== (doc.notes ?? null)) patch.notes = notes.trim() || null
  if (JSON.stringify(custom) !== JSON.stringify(doc.custom_fields ?? {})) patch.custom_fields = custom
  const changes = Object.keys(patch).length

  const keys = rows.map((r) => r.key.trim()).filter(Boolean)
  const keyError = (r: FieldRow): string | undefined => {
    const k = r.key.trim()
    if (!k) return r.text.trim() ? 'Name the field.' : undefined
    if (!KEY_PATTERN.test(k)) return 'Lower-case letters, digits and _, starting with a letter; up to 40.'
    if (keys.filter((x) => x === k).length > 1) return 'Already used above.'
    return undefined
  }
  const titleError = !title.trim() ? 'A document needs a title.' : title.trim().length > 255 ? 'Up to 255 characters.' : undefined
  const notesError = notes.length > 4000 ? 'Up to 4,000 characters.' : undefined
  const rowsError = rows.some((r) => keyError(r) || r.text.length > 500)
  const invalid = Boolean(titleError || notesError || rowsError || keys.length > MAX_FIELDS)

  if (!editable) {
    return (
      <div className="space-y-5">
        <DescriptionList>
          <Detail label="Title" value={doc.title} wide />
          <Detail label="Type" value={documentTypeLabel(doc.document_type, types)} />
          <Detail label="Document date" value={doc.document_date ? formatDate(doc.document_date) : null} emptyLabel="Not set" />
          <Detail label="Notes" value={doc.notes} emptyLabel="None" wide />
          {Object.entries(doc.custom_fields ?? {}).map(([key, value]) => (
            <Detail key={key} label={key.replace(/_/g, ' ')} value={shown(value)} emptyLabel="Not set" />
          ))}
        </DescriptionList>
        <Facts doc={doc} workspaceName={workspaceName} uploader={uploader} />
      </div>
    )
  }

  const save = async () => {
    setTried(true)
    if (invalid || changes === 0) return
    setBusy(true)
    setError('')
    try {
      onSaved(await client.documents.update(doc.id, patch))
    } catch (e) {
      setError(refusalText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <form
        noValidate
        className="space-y-4"
        aria-label="Document details"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {error && <Alert tone="error">{error}</Alert>}
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={tried ? titleError : undefined} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value)} hint="What you say it is — ProDoc does not guess.">
            {!types.some((t) => t.code === doc.document_type) && <option value={doc.document_type}>{documentTypeLabel(doc.document_type, types)}</option>}
            {types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </Select>
          <Input label="Document date" type="date" value={date} onChange={(e) => setDate(e.target.value)} hint="The date on the document." />
        </div>
        <Textarea label="Notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} error={notesError} />
        <CustomFields
          rows={rows}
          keyError={keyError}
          onChange={setRows}
          onAdd={() => {
            setRows([...rows, { id: nextId, key: '', text: '' }])
            setNextId((n) => n + 1)
          }}
          full={keys.length >= MAX_FIELDS}
        />
        <div className="flex items-center justify-end gap-3">
          <span className="text-muted-foreground text-xs">{changes ? `${plural(changes, 'change')} not saved` : 'No changes'}</span>
          <Button type="submit" loading={busy} disabled={changes === 0}>
            Save details
          </Button>
        </div>
      </form>
      <Facts doc={doc} workspaceName={workspaceName} uploader={uploader} />
    </div>
  )
}

/**
 * Custom fields — a name and a value per row. Composed from the kit's `Input`
 * and `Button`: the kit's `KeyValueEditor` at the pin takes digits only (it is
 * the usage-limit editor), and a custom field is text, a date or an amount
 * (plan §7: where the pin lacks a control, compose it from kit primitives).
 */
function CustomFields({
  rows,
  keyError,
  onChange,
  onAdd,
  full,
}: {
  rows: FieldRow[]
  keyError: (r: FieldRow) => string | undefined
  onChange: (rows: FieldRow[]) => void
  onAdd: () => void
  full: boolean
}) {
  const set = (id: number, change: Partial<FieldRow>) => onChange(rows.map((r) => (r.id === id ? { ...r, ...change } : r)))
  return (
    <fieldset className="space-y-3">
      <legend className="text-foreground text-sm font-medium">Custom fields</legend>
      {rows.length === 0 && <p className="text-muted-foreground text-xs">None yet — a reference number, a period, a party: whatever your organisation files by.</p>}
      {rows.map((r) => (
        <div key={r.id} className="grid items-start gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto]">
          <Input size="sm" aria-label="Field name" placeholder="po_number" value={r.key} onChange={(e) => set(r.id, { key: e.target.value })} error={keyError(r)} spellCheck={false} />
          <Input
            size="sm"
            aria-label={`Value of ${r.key || 'this field'}`}
            value={r.text}
            onChange={(e) => set(r.id, { text: e.target.value })}
            error={r.text.length > 500 ? 'Up to 500 characters.' : undefined}
          />
          <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${r.key || 'this field'}`} onClick={() => onChange(rows.filter((x) => x.id !== r.id))}>
            <Trash2 size={14} aria-hidden />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={full}>
        <Plus size={14} aria-hidden />
        Add a field
      </Button>
    </fieldset>
  )
}

/** What ProDoc knows about the bytes and their place — facts, not edits. */
function Facts({ doc, workspaceName, uploader }: { doc: ProDocDocument; workspaceName: string | null; uploader: string }) {
  return (
    <div>
      <CardHeader title="The file" className="px-0 pt-0" />
      <DescriptionList>
        <Detail label="File name" value={doc.file_name} wide />
        <Detail label="Workspace" value={workspaceName} emptyLabel="—" />
        <Detail label="Folder" value={folderLine(doc)} wide />
        <Detail label="Owner app" value={doc.owner_app} />
        <Detail label="Source" value={doc.source_ref} emptyLabel="Uploaded here" />
        <Detail label="Kind" value={mediaLabel(doc.media_type)} />
        <Detail label="Size" value={formatBytes(doc.size_bytes)} />
        {doc.page_count !== null && doc.page_count !== undefined && <Detail label="Pages" value={String(doc.page_count)} />}
        <Detail label="Encrypted" value={doc.encrypted ? 'Yes — stored exactly as it is' : 'No'} />
        <Detail
          label="SHA-256"
          value={
            <span className="font-mono text-xs break-all" title={doc.sha256}>
              {doc.sha256}
            </span>
          }
          wide
        />
        <Detail label="Uploaded by" value={uploader.charAt(0).toUpperCase() + uploader.slice(1)} />
        <Detail label="Uploaded" value={formatDateTime(doc.created_at)} />
        <Detail label="Review" value={reviewLine(doc)} wide />
      </DescriptionList>
    </div>
  )
}

/** Where it is filed, as a path from the workspace's top level — the single read's breadcrumb. */
function folderLine(doc: ProDocDocument): string {
  if (!doc.folder_id) return 'Top level of the workspace'
  const crumbs = doc.breadcrumb ?? []
  return crumbs.length ? crumbs.map((c) => c.name).join(' › ') : 'A folder'
}

/** Where the four-eyes state stands, said as a line: the state, and when it got there. */
function reviewLine(doc: ProDocDocument): string {
  const state = doc.review_state ?? 'uploaded'
  if (state === 'needs_review') return `Needs review since ${formatDateTime(doc.review_requested_at ?? doc.updated_at)}`
  if ((state === 'approved' || state === 'rejected') && doc.reviewed_at) {
    return `${reviewLabel(state)} ${formatDateTime(doc.reviewed_at)}${doc.review_comment ? ` — “${doc.review_comment}”` : ''}`
  }
  return state === 'uploaded' ? 'Not reviewed — nothing asked of a reviewer' : reviewLabel(state)
}

/* -------------------------------------------------------------- versions */

const VERSION_REASON: Record<string, string> = {
  initial: 'First upload',
  replacement: 'Replaced',
  resubmission: 'Re-submitted',
  duplicate_linked: 'Linked from a duplicate upload',
  app_supersede: 'Superseded by its app',
  imported: 'Imported',
}

/**
 * Why a version exists, as a line says it. An **imported** one (FR-42) is
 * history its app brought, stored at its original upload time — so the line
 * says when that was, not when the import ran.
 */
export function versionReasonText(reason: string, originallyUploaded: string | null | undefined): string {
  if (reason === 'imported' && originallyUploaded) return `Imported, originally uploaded ${formatDate(originallyUploaded)}`
  return VERSION_REASON[reason] ?? reason
}

/** Every version, newest first — each one immutable, each readable by a logged read (FR-9). */
function VersionsPanel({
  client,
  doc,
  versions,
  userId,
  canAdd,
  onAdd,
}: {
  client: ProDocClient
  doc: ProDocDocument
  versions: Loading<DocumentVersion[]>
  userId: string | null | undefined
  canAdd: boolean
  onAdd: () => void
}) {
  const { toast } = useToast()
  const list = [...(versions.data ?? [])].sort((a, b) => b.version_no - a.version_no)

  const download = async (v: DocumentVersion) => {
    try {
      const handle = await client.documents.versions.file(doc.id, v.id, 'download')
      saveBlob(await handle.blob(), handle.fileName ?? v.file_name)
    } catch (e) {
      toast({ message: errorText(e, 'That version could not be downloaded.'), tone: 'error' })
    }
  }

  return (
    <div className="space-y-4">
      {canAdd && (
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus size={14} aria-hidden />
          Upload a new version
        </Button>
      )}
      {versions.loading && !versions.data ? (
        <SkeletonRegion label="Loading the versions" className="space-y-3">
          <SkeletonText />
          <SkeletonText w="w-2/3" />
        </SkeletonRegion>
      ) : versions.error && !versions.data ? (
        <Alert tone="error">
          Could not load the versions: {versions.error}{' '}
          <Button variant="link" onClick={() => void versions.reload()}>
            Try again
          </Button>
        </Alert>
      ) : list.length === 0 ? (
        <p className="text-muted-foreground text-sm">No versions are recorded for this document.</p>
      ) : (
        <ol className="divide-border divide-y" aria-label="Versions">
          {list.map((v) => (
            <li key={v.id} className="flex items-start gap-3 py-3">
              <FileMark mediaType={v.media_type} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-foreground flex flex-wrap items-center gap-2 text-sm font-medium">
                  Version {v.version_no}
                  {(v.is_current || v.id === doc.current_version_id) && (
                    <Badge tone="success" size="sm">
                      Current
                    </Badge>
                  )}
                  {v.encrypted && (
                    <Badge tone="warning" size="sm">
                      Encrypted
                    </Badge>
                  )}
                </p>
                <p className="text-muted-foreground truncate text-xs" title={v.file_name}>
                  {v.file_name} · {mediaLabel(v.media_type)} · {formatBytes(v.size_bytes)}
                  {v.declared_media_type && v.declared_media_type !== v.media_type ? ` · sent as ${mediaLabel(v.declared_media_type)}` : ''}
                </p>
                <p className="text-muted-foreground text-xs">
                  {/* An imported version's time is its original upload, said once, in the reason (D40). */}
                  {versionReasonText(v.reason, v.created_at)} · {versionUploaderLabel(v, userId)}
                  {v.reason === 'imported' ? '' : `, ${when(v.created_at)}`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => void download(v)} aria-label={`Download version ${v.version_no}`}>
                <Download size={14} aria-hidden />
              </Button>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- history */

const ACTION_LABEL: Record<string, string> = {
  'document.uploaded': 'Uploaded',
  'document.read': 'Read',
  'document.metadata_changed': 'Details changed',
  'document.updated': 'Details changed',
  'document.version_added': 'New version',
  'document.moved': 'Moved',
  'document.archived': 'Archived',
  'document.restored': 'Restored',
  'document.duplicate_resolved': 'Duplicate choice recorded',
  'document.review_requested': 'Sent for review',
  'document.tags_changed': 'Tags changed',
  'document.linked': 'Linked to a document',
  'document.unlinked': 'Unlinked from a document',
  'document.approved': 'Approved',
  'document.rejected': 'Rejected',
}

const READ_LABEL: Record<string, string> = {
  view: 'Viewed',
  download: 'Downloaded',
  zip: 'Downloaded in a ZIP',
  share: 'Opened through a share link',
  app: 'Read by an app',
  extraction: 'Read by ProDoc to extract its fields',
  // W23 (FR-48, FR-52): the scan for personal data, and a person revealing one masked value.
  scan: 'Checked by ProDoc for personal data',
  reveal: 'Revealed a masked value',
}

const FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  document_type: 'Type',
  document_date: 'Document date',
  notes: 'Notes',
  custom_fields: 'Custom fields',
  workspace_id: 'Workspace',
  status: 'Status',
  file_name: 'File name',
  media_type: 'Kind',
  version_no: 'Version',
  review_state: 'Review',
  folder_id: 'Folder',
  tag_ids: 'Tags',
  review_comment: 'Comment',
  comment: 'Comment',
  four_eyes: 'Four-eyes',
}

/** How a document came to be waiting for review — `document.review_requested`'s reason (W4). */
const REVIEW_CAUSE: Record<string, string> = {
  upload: 'Its type is reviewed on upload',
  request: 'Asked for by a person',
  new_version: 'A new version arrived',
}

/**
 * Who did it, as a reader says it. An app first, and with whom it acted for
 * (FR-40) — "ProDesk for Asha Rao", "ProDesk, for a client contact" — because
 * the member's id and name on an app's row are whom it acted for, not who did it.
 */
export function actorOf(entry: AuditEntry, userId: string | null | undefined): string {
  const actor = entry.actor
  if (actor?.kind === 'app') return actedForLabel(actor, userId)
  if (actor?.user_id && userId && actor.user_id === userId) return 'You'
  if (actor?.display_name) return actor.display_name
  if (actor?.kind === 'link') return 'Someone with a share link'
  if (actor?.kind === 'system') return 'ProDoc'
  return 'A member'
}

const CHOICE: Record<string, string> = {
  keep_both: 'kept both',
  new_version: 'linked as a new version',
  discard: 'discarded the new copy',
}

/** One changed field, said plainly: `Title: old → new`, or `Type: Invoice` when it had no value before. */
interface ChangeLine {
  field: string
  old?: unknown
  new?: unknown
  plain?: unknown
}

/** An imported version's original upload time (FR-42), from its audit row's `changes.uploaded_at` — the value, or its `new`. */
function originallyUploaded(changes: Record<string, unknown> | undefined): string | null {
  const at = changes?.uploaded_at
  const value = at !== null && typeof at === 'object' && 'new' in at ? (at as { new?: unknown }).new : at
  return typeof value === 'string' && value ? value : null
}

/** What the row's reason says: why it waits for review, or why the version exists — an import with its original date. */
function reasonText(e: AuditEntry): string | null {
  if (!e.reason) return null
  if (e.action === 'document.review_requested') return REVIEW_CAUSE[e.reason] ?? `“${e.reason}”`
  if (e.reason === 'imported') return versionReasonText('imported', originallyUploaded(e.changes))
  return VERSION_REASON[e.reason] ?? `“${e.reason}”`
}

/** D59: the approval's `changes.own_upload` — `true`, or `{new: true}` — said in its title, never as a changed field. */
export function ownUpload(changes: Record<string, unknown> | undefined): boolean {
  const value = changes?.own_upload
  return value === true || (value !== null && typeof value === 'object' && (value as { new?: unknown }).new === true)
}

function changeLines(changes: Record<string, unknown> | undefined, reason?: string | null): ChangeLine[] {
  if (!changes) return []
  return Object.entries(changes)
    // A decision without a comment records `comment: null` — nothing said, so no line.
    .filter(([field, change]) => field !== 'sha256' && field !== 'duplicate_of' && field !== 'choice' && field !== 'own_upload' && change !== null && change !== undefined)
    // An import's original time is said by its reason line, once.
    .filter(([field]) => !(reason === 'imported' && field === 'uploaded_at'))
    .map(([field, change]) =>
      change !== null && typeof change === 'object' && !Array.isArray(change) && ('old' in change || 'new' in change)
        ? { field, old: (change as { old?: unknown }).old, new: (change as { new?: unknown }).new }
        : { field, plain: change },
    )
}

/** The history: every edit with old → new and its actor, every read with its purpose — newest first (FR-11, FR-18). */
function HistoryPanel({
  client,
  doc,
  revision,
  userId,
  types,
  nameOf,
  tagName,
}: {
  client: ProDocClient
  doc: ProDocDocument
  revision: number
  userId: string | null | undefined
  types: DocumentTypeOption[]
  nameOf: (id: string | null | undefined) => string | null
  tagName: (id: string) => string | null
}) {
  const first = useLoad(() => client.documents.audit(doc.id, { limit: 50 }), [client, doc.id, revision])
  const [older, setOlder] = useState<AuditEntry[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [more, setMore] = useState<'idle' | 'loading' | 'error'>('idle')
  useEffect(() => {
    setOlder([])
    setNext(first.data?.next ?? null)
  }, [first.data])

  const value = (field: string, v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—'
    if (field === 'document_type' && typeof v === 'string') return documentTypeLabel(v, types)
    if (field === 'workspace_id' && typeof v === 'string') return nameOf(v) ?? 'another workspace'
    if (field === 'folder_id') return typeof v === 'string' ? 'a folder' : 'the top level'
    if (field === 'tag_ids' && Array.isArray(v)) {
      return v.map((id) => (typeof id === 'string' ? (tagName(id) ?? 'a tag since deleted') : '')).filter(Boolean).join(', ') || 'none'
    }
    if (field === 'document_date' && typeof v === 'string') return formatDate(v)
    if (field === 'media_type' && typeof v === 'string') return mediaLabel(v)
    if (field === 'status' && typeof v === 'string') return v === 'archived' ? 'Archived' : v === 'stored' ? 'Stored' : v
    if (field === 'review_state' && typeof v === 'string') return reviewLabel(v)
    if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${shown(x as CustomValue)}`).join(', ') || '—'
    return shown(v as CustomValue)
  }
  const title = (e: AuditEntry): string => {
    if (e.kind === 'read') return READ_LABEL[e.purpose ?? ''] ?? 'Read'
    // W22 (FR-23 refined, D59): with four-eyes off, an approver may approve the version they uploaded — said so.
    if (e.action === 'document.approved' && ownUpload(e.changes)) return 'Approved their own upload'
    const label = ACTION_LABEL[e.action ?? ''] ?? (e.action ?? 'Changed').split('.').pop()!.replace(/_/g, ' ')
    const choice = e.action === 'document.duplicate_resolved' && typeof e.changes?.choice === 'string' ? CHOICE[e.changes.choice] : undefined
    return choice ? `${label} — ${choice}` : label
  }

  if (first.loading && !first.data) {
    return (
      <SkeletonRegion label="Loading the history" className="space-y-3">
        <SkeletonText />
        <SkeletonText w="w-2/3" />
        <SkeletonText w="w-1/2" />
      </SkeletonRegion>
    )
  }
  if (first.error && !first.data) {
    return (
      <Alert tone="error">
        Could not load the history: {first.error}{' '}
        <Button variant="link" onClick={() => void first.reload()}>
          Try again
        </Button>
      </Alert>
    )
  }
  const entries = [...(first.data?.items ?? []), ...older]
  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm">Nothing is recorded for this document yet.</p>
  }

  const loadOlder = async () => {
    if (!next) return
    setMore('loading')
    try {
      const page = await client.documents.audit(doc.id, { before: next, limit: 50 })
      setOlder((o) => [...o, ...page.items])
      setNext(page.next)
      setMore('idle')
    } catch {
      setMore('error')
    }
  }

  return (
    <div className="space-y-4">
      <ol className="space-y-3" aria-label="History">
        {entries.map((e) => (
          <li key={`${e.kind}:${e.id}`} className="border-subtle border-l-2 pl-3">
            <p className="text-foreground text-sm">{title(e)}</p>
            {changeLines(e.changes, e.reason).length > 0 && (
              <ul className="text-muted-foreground mt-0.5 space-y-0.5 text-xs">
                {changeLines(e.changes, e.reason).map((c) => (
                  <li key={c.field}>
                    {FIELD_LABEL[c.field] ?? c.field.replace(/_/g, ' ')}:{' '}
                    {'plain' in c
                      ? value(c.field, c.plain)
                      : c.old === null || c.old === undefined
                        ? value(c.field, c.new)
                        : `${value(c.field, c.old)} → ${value(c.field, c.new)}`}
                  </li>
                ))}
              </ul>
            )}
            {reasonText(e) && <p className="text-muted-foreground mt-0.5 text-xs">{reasonText(e)}</p>}
            <p className="text-muted-foreground text-xs">
              {actorOf(e, userId)} · {formatDateTime(e.occurred_at)}
            </p>
          </li>
        ))}
      </ol>
      {next && (
        <Button variant="outline" size="sm" loading={more === 'loading'} onClick={() => void loadOlder()}>
          {more === 'error' ? 'Could not load — try again' : 'Show older'}
        </Button>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- dialogs */

/** The words for a refusal the person can act on (plan §5.1's reasons). */
export function refusalText(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.reason) {
      case 'same_as_current':
        return 'That file is the same as the current version — nothing changed.'
      case 'archived':
        return 'An archived document cannot be changed. Restore it first.'
      case 'corrupt':
        return `The file is damaged: ${e.message}`
      case 'unsupported_type':
        return 'That is not a type ProDoc stores — its contents decide, not its name.'
      case 'illegal_transition':
        return 'Its review has moved on since this page was loaded. Reload to see where it stands.'
    }
  }
  return errorText(e)
}

/** A replacement file: a new version, the current one kept (FR-9). */
function VersionDialog({
  open,
  current,
  accept,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  current: ProDocDocument
  accept: string[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (file: File) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  useEffect(() => {
    if (open) setFile(null)
  }, [open])
  return (
    <Dialog open={open} onClose={onClose} title="Upload a new version" error={error}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (file) onSave(file)
        }}
      >
        <p className="text-muted-foreground text-sm">
          The new file becomes the current version of “{current.title}”. Version {current.version_count} and every earlier one are kept, and each stays
          readable.
        </p>
        <FileInput label="File" onFile={setFile} accept={acceptFor(accept) || undefined} hint="Its contents decide its type, as for any upload." />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={!file}>
            Upload version
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * Refile a document: another folder of its workspace, or another workspace —
 * whose people see it — where it lands in the top level unless a folder there
 * is chosen too (FR-16, FR-38).
 */
function MoveDialog({
  open,
  client,
  current,
  app,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  client: ProDocClient
  current: ProDocDocument
  app: string
  busy: boolean
  error: string
  onClose: () => void
  onSave: (workspaceId: string, folderId: string | null) => void
}) {
  const workspaces = useLoad(() => (open ? client.workspaces.list() : Promise.resolve([])), [client, open])
  const [workspace, setWorkspace] = useState(current.workspace_id)
  const [folder, setFolder] = useState<string | null>(current.folder_id ?? null)
  useEffect(() => {
    if (open) {
      setWorkspace(current.workspace_id)
      setFolder(current.folder_id ?? null)
    }
  }, [open, current.workspace_id, current.folder_id])
  const unchanged = workspace === current.workspace_id && folder === (current.folder_id ?? null)
  const name = (workspaces.data ?? []).find((w) => w.id === workspace)?.name ?? 'the workspace'
  return (
    <Dialog open={open} onClose={onClose} title="Move to a folder or workspace" error={error}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onSave(workspace, folder)
        }}
        className="space-y-5"
      >
        <Select
          label="Workspace"
          value={workspace}
          onChange={(e) => {
            setWorkspace(e.target.value)
            // A folder belongs to one workspace: another workspace starts at its top level.
            setFolder(null)
          }}
          hint="Everyone who sees that workspace sees it from now on; whoever uploaded it still does."
          error={workspaces.error ? `Could not load workspaces: ${workspaces.error}` : undefined}
        >
          {!(workspaces.data ?? []).some((w) => w.id === current.workspace_id) && <option value={current.workspace_id}>Its workspace now</option>}
          {(workspaces.data ?? [])
            .filter((w) => w.status !== 'archived')
            .map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.is_default ? ' (default)' : ''}
              </option>
            ))}
        </Select>
        <FolderPicker client={client} workspaceId={open ? workspace : null} value={folder} onChange={setFolder} rootLabel={`Top level of ${name}`} app={app} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={unchanged}>
            Move
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/* --------------------------------------------------------------- related */

const RELATION_LABEL: Record<RelationKind, string> = {
  source_ref: 'Same reference',
  sha256: 'Same file',
  link: 'Linked',
}

/**
 * Related documents (FR-18): the same `source_ref` (one record of a sibling
 * app), the same bytes (a second copy someone kept), and the documents a
 * person linked — every one within the reader's sight. Whoever may edit this
 * document links another or unlinks one; each is audited at both ends.
 */
function RelatedPanel({
  client,
  doc,
  editable,
  revision,
  onOpen,
  onChanged,
}: {
  client: ProDocClient
  doc: ProDocDocument
  editable: boolean
  revision: number
  onOpen?: (document: ProDocDocument) => void
  onChanged: () => void
}) {
  const { toast } = useToast()
  const [reloads, setReloads] = useState(0)
  const related = useLoad(() => client.documents.related(doc.id), [client, doc.id, revision, reloads])
  const [linking, setLinking] = useState(false)
  const [unlinking, setUnlinking] = useState<string | null>(null)

  const unlink = async (other: ProDocDocument) => {
    setUnlinking(other.id)
    try {
      await client.documents.links.remove(doc.id, other.id)
      setReloads((n) => n + 1)
      onChanged()
      toast(`Unlinked “${other.title}”.`)
    } catch (e) {
      toast({ message: errorText(e), tone: 'error' })
    } finally {
      setUnlinking(null)
    }
  }

  const list = related.data ?? []
  return (
    <div className="space-y-4">
      {editable && (
        <Button variant="outline" size="sm" onClick={() => setLinking(true)}>
          <Link2 size={14} aria-hidden />
          Link a document…
        </Button>
      )}
      {related.loading && !related.data ? (
        <SkeletonRegion label="Loading related documents" className="space-y-3">
          <SkeletonText />
          <SkeletonText w="w-2/3" />
        </SkeletonRegion>
      ) : related.error && !related.data ? (
        <Alert tone="error">
          Could not load the related documents: {related.error}{' '}
          <Button variant="link" onClick={() => void related.reload()}>
            Try again
          </Button>
        </Alert>
      ) : list.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing related yet. Documents with the same reference or the same file appear here on their own{editable ? ', and any you link' : ''}.
        </p>
      ) : (
        <ul className="divide-border divide-y" aria-label="Related documents">
          {list.map(({ document: other, relations }) => (
            <li key={other.id} className="flex items-start gap-3 py-3">
              <FileMark mediaType={other.media_type} size="sm" />
              <div className="min-w-0 flex-1">
                {onOpen ? (
                  <Button variant="link" className="h-auto max-w-full justify-start truncate px-0 text-left" onClick={() => onOpen(other)} title={other.title}>
                    {other.title}
                  </Button>
                ) : (
                  <p className="text-foreground truncate text-sm font-medium">{other.title}</p>
                )}
                <p className="mt-1 flex flex-wrap items-center gap-1.5">
                  {relations.map((r) => (
                    <Badge key={r} size="sm" tone={r === 'link' ? 'info' : 'neutral'}>
                      {RELATION_LABEL[r]}
                    </Badge>
                  ))}
                  {other.created_at && <span className="text-muted-foreground text-xs">uploaded {when(other.created_at)}</span>}
                </p>
              </div>
              {editable && relations.includes('link') && (
                <Button variant="ghost" size="sm" onClick={() => void unlink(other)} loading={unlinking === other.id} aria-label={`Unlink ${other.title}`}>
                  Unlink
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <LinkDialog
        open={linking}
        client={client}
        doc={doc}
        already={new Set(list.filter((r) => r.relations.includes('link')).map((r) => r.document.id))}
        onClose={() => setLinking(false)}
        onLinked={(other) => {
          setLinking(false)
          setReloads((n) => n + 1)
          onChanged()
          toast(`Linked “${other.title}”.`)
        }}
      />
    </div>
  )
}

/** Find a document by the register's own search and link it — one row per pair, whichever side it is made from. */
function LinkDialog({
  open,
  client,
  doc,
  already,
  onClose,
  onLinked,
}: {
  open: boolean
  client: ProDocClient
  doc: ProDocDocument
  already: Set<string>
  onClose: () => void
  onLinked: (other: ProDocDocument) => void
}) {
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    if (open) {
      setDraft('')
      setQ('')
      setError('')
    }
  }, [open])
  const found = useLoad(() => (open && q.length >= 2 ? client.documents.list({ q, status: 'stored', limit: 8 }) : Promise.resolve(null)), [client, open, q])
  const rows = (found.data?.items ?? []).filter((d) => d.id !== doc.id)

  const link = async (other: ProDocDocument) => {
    setBusy(other.id)
    setError('')
    try {
      await client.documents.links.add(doc.id, other.id)
      onLinked(other)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Link a document" error={error} width="lg">
      <div className="space-y-4">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(draft.trim())
          }}
        >
          <SearchInput value={draft} onChange={setDraft} label="Find a document" placeholder="Title, file name, reference… then Enter" />
        </form>
        {q.length < 2 ? (
          <p className="text-muted-foreground text-sm">Type at least two letters of its title, file name or reference, then press Enter.</p>
        ) : found.loading && !found.data ? (
          <SkeletonRegion label="Searching" className="space-y-2">
            <SkeletonText />
            <SkeletonText w="w-2/3" />
          </SkeletonRegion>
        ) : found.error ? (
          <Alert tone="error">Could not search: {found.error}</Alert>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No document you can see matches “{q}”.</p>
        ) : (
          <ul className="divide-border divide-y" aria-label="Search results">
            {rows.map((other) => (
              <li key={other.id} className="flex items-center gap-3 py-2">
                <FileMark mediaType={other.media_type} size="sm" />
                <p className="text-foreground min-w-0 flex-1 truncate text-sm" title={other.title}>
                  {other.title}
                </p>
                {already.has(other.id) ? (
                  <span className="text-muted-foreground text-xs">Linked</span>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => void link(other)} loading={busy === other.id}>
                    Link
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  )
}

/** Hand a blob to the browser as a file. */
function saveBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}
